 import * as React from "react";
import { 
    ApolloError, 
    gql, 
    useMutation, 
} from "@apollo/client";
import { 
    CreateCampaignMutationMutation,
    CreateCampaignMutationMutationVariables 
} from "../../../../../schemaTypes";
import { NormalizedErrorMap } from "../../../../../types/NormalizedErrorMap";
import { normalizeErrors } from "../../../../../utils/normalizeErrors";

export const createCampaignMutation = gql`
  mutation CreateCampaignMutation(
    $title: String
    $description: String
    $question: String
    $active: Boolean!
    $bannerUrl: String
    $locationId: [String!]!
    $communityId: [String!]!
    $campaignType: String!
    $pollOptions: [String]
    $campaignLink: String
    $startDate: String!
    $endDate: String!
    $colorCode: String
  ) {
    createCampaign(
      title: $title
      description: $description
      question: $question
      active: $active
      bannerUrl: $bannerUrl
      locationId: $locationId
      communityId: $communityId
      campaignType: $campaignType
      pollOptions: $pollOptions
      campaignLink: $campaignLink
      startDate: $startDate
      endDate: $endDate
      colorCode: $colorCode
    ) {
      errors {
        path
        message
      }
      message
      bannerUrl
    }
  }
`;

interface Props {
  onMessage?: (message: string) => void;
  children: (
    data: {
      submit: (
        values: CreateCampaignMutationMutationVariables & {
          bannerFile?: File;
        }
      ) => Promise<{
        errors: NormalizedErrorMap | null;
        message?: string;
      }>;
      loading: boolean;
      error?: ApolloError;
    }
  ) => React.ReactElement | null;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export const CreateCampaignController: React.FC<Props> = ({ children, onMessage }) => {
  const [_createCampaignMutation, { loading, error }] = useMutation<
    CreateCampaignMutationMutation,
    CreateCampaignMutationMutationVariables
  >(createCampaignMutation);

  const submit = async (
    values: CreateCampaignMutationMutationVariables & {
      bannerFile?: File;
    }
  ) => {
    const { bannerFile, ...rest } = values;
    const normalizeIds = (ids?: CreateCampaignMutationMutationVariables["locationId"] | null) => {
      const idList = Array.isArray(ids) ? ids : typeof ids === "string" ? [ids] : [];

      return idList
        .filter((id): id is string => typeof id === "string")
        .map((id) => id.trim())
        .filter(Boolean);
    };

    const locationId = normalizeIds(rest.locationId);
    if (locationId.length === 0) {
      return {
        errors: normalizeErrors([
          { path: "locationId", message: "At least one location is required" },
        ]),
        message: undefined,
      };
    }

    const communityId = normalizeIds(rest.communityId);
    if (communityId.length === 0) {
      return {
        errors: normalizeErrors([
          { path: "communityId", message: "At least one community is required" },
        ]),
        message: undefined,
      };
    }

    let bannerUrl = rest.bannerUrl;
    if (bannerFile) {
      bannerUrl = await fileToBase64(bannerFile);
    }

    // Directly send all values to backend, let backend handle all business logic and validation
    const { data } = await _createCampaignMutation({
      variables: {
        ...rest,
        locationId,
        communityId,
        bannerUrl,
      },
    });

    const errors = data?.createCampaign?.errors
      ? normalizeErrors(data.createCampaign.errors)
      : null;

    if (data?.createCampaign?.message && onMessage) {
      onMessage(data.createCampaign.message);
    }

    return {
      errors,
      message: data?.createCampaign?.message ?? undefined,
      bannerUrl: data?.createCampaign?.bannerUrl ?? undefined,
    };
  };

  return children({ submit, loading, error });
};